/**
 * App-wide constants
 */

export const TIMER_PRESETS = {
    pomodoro: { label: 'Pomodoro', work: 25 * 60, break: 5 * 60, icon: '🍅' },
    longPomodoro: { label: 'Deep Work', work: 50 * 60, break: 10 * 60, icon: '🧠' },
    hour: { label: '1 Hour', work: 60 * 60, break: 15 * 60, icon: '⏰' },
    unlimited: { label: 'Unlimited', work: Infinity, break: 0, icon: '♾️' },
};

export const DEFAULT_TAGS = [
    'deep-work', 'shallow-work', 'creative', 'learning',
    'planning', 'coding', 'writing', 'reading', 'exercise', 'meditation'
];

export const IDENTITY_COLORS = [
    '#2383E2', '#06B6D4', '#10B981', '#F59E0B', '#EF4444',
    '#EC4899', '#8B5CF6', '#14B8A6', '#6366F1', '#F97316',
    '#84CC16', '#0EA5E9',
];

export const IDENTITY_EMOJIS = [
    '🧑‍💻', '🎨', '📚', '🏋️', '🧘', '✍️', '🎯', '🚀',
    '💡', '🔬', '🎵', '📊', '🧠', '⚡', '🌟', '🔥'
];

export const XP_PER_MINUTE = 4;
export const STREAK_MULTIPLIER = 0.1; // +10% per streak day, max 2x
export const MAX_STREAK_MULTIPLIER = 2.0;
export const COMPLETION_BONUS = 50; // bonus XP for completing full session
export const CIRCUIT_COMPLETION_BONUS = 100; // flat bonus for finishing a whole Circuit (all steps), on top of per-step session XP

// XP for completing a Review ceremony (Reviews is a gated plugin — awarded only when enabled).
export const REVIEW_XP = { weekly: 50, quarterly: 200 };

export const LEVEL_THRESHOLDS = [
    0, 100, 250, 500, 1000, 1750, 2750, 4000, 5500, 7500,
    10000, 13000, 16500, 20500, 25000, 30000, 36000, 42500, 50000, 60000
];

export const LEVEL_NAMES = [
    'Novice',        // 1
    'Apprentice',    // 2
    'Initiate',      // 3
    'Student',       // 4
    'Adept',         // 5
    'Scholar',       // 6
    'Practitioner',  // 7
    'Specialist',    // 8
    'Expert',        // 9
    'Veteran',       // 10
    'Master',        // 11
    'Grandmaster',   // 12
    'Sage',          // 13
    'Oracle',        // 14
    'Luminary',      // 15
    'Virtuoso',      // 16
    'Legend',        // 17
    'Mythic',        // 18
    'Transcendent',  // 19
    'Ascendant',     // 20
];

export const ACHIEVEMENTS = [
    { id: 'first-session', name: 'First Step', description: 'Complete your first focus session', icon: '🎯', condition: (stats) => stats.totalSessions >= 1 },
    { id: 'first-circuit', name: 'Full Circuit', description: 'Complete your first full Circuit', icon: '🔁', condition: (stats) => stats.totalCircuitsCompleted >= 1 },
    { id: '10-sessions', name: 'Getting Started', description: 'Complete 10 focus sessions', icon: '🔟', condition: (stats) => stats.totalSessions >= 10 },
    { id: '50-sessions', name: 'Dedicated', description: 'Complete 50 focus sessions', icon: '💪', condition: (stats) => stats.totalSessions >= 50 },
    { id: '100-sessions', name: 'Centurion', description: 'Complete 100 focus sessions', icon: '💯', condition: (stats) => stats.totalSessions >= 100 },
    { id: '3-day-streak', name: 'On a Roll', description: 'Maintain a 3-day streak', icon: '🔥', condition: (stats) => stats.currentStreak >= 3 },
    { id: '7-day-streak', name: 'Week Warrior', description: 'Maintain a 7-day streak', icon: '⚡', condition: (stats) => stats.currentStreak >= 7 },
    { id: '30-day-streak', name: 'Unstoppable', description: 'Maintain a 30-day streak', icon: '🌟', condition: (stats) => stats.currentStreak >= 30 },
    { id: '10-hours', name: 'Ten Hours', description: 'Accumulate 10 hours of focus', icon: '⏱️', condition: (stats) => stats.totalFocusTime >= 36000 },
    { id: '50-hours', name: 'Half Century', description: 'Accumulate 50 hours of focus', icon: '🏆', condition: (stats) => stats.totalFocusTime >= 180000 },
    { id: '100-hours', name: 'Master', description: 'Accumulate 100 hours of focus', icon: '👑', condition: (stats) => stats.totalFocusTime >= 360000 },
    { id: 'level-5', name: 'Rising Star', description: 'Reach Level 5', icon: '⭐', condition: (stats) => stats.level >= 5 },
    { id: 'level-10', name: 'Elite', description: 'Reach Level 10', icon: '🌠', condition: (stats) => stats.level >= 10 },
    { id: 'first-dump', name: 'Brain Dumper', description: 'Complete your first voice dump', icon: '🎙️', condition: (stats) => stats.totalVoiceDumps >= 1 },
    { id: '10-dumps', name: 'Vocal Virtuoso', description: 'Complete 10 voice dumps', icon: '🗣️', condition: (stats) => stats.totalVoiceDumps >= 10 },
];

export const SHOP_ITEMS = [
    { id: 'theme-midnight', name: 'Midnight', type: 'theme', cost: 500, description: 'Deep blue and purple vibes.', value: 'theme-midnight' },
    { id: 'theme-forest', name: 'Forest', type: 'theme', cost: 500, description: 'Calming greens and browns.', value: 'theme-forest' },
    { id: 'theme-cyberpunk', name: 'Cyberpunk', type: 'theme', cost: 1000, description: 'Neon pinks and blues.', value: 'theme-cyberpunk' },
    { id: 'theme-sunset', name: 'Sunset', type: 'theme', cost: 750, description: 'Warm oranges and reds.', value: 'theme-sunset' },
    { id: 'avatar-dragon', name: 'Dragon', type: 'avatar', cost: 2000, description: 'Unleash the beast.', value: '🐉' },
    { id: 'avatar-wizard', name: 'Wizard', type: 'avatar', cost: 2000, description: 'You are a wizard, Harry.', value: '🧙‍♂️' },
];
